import { useMemo, useRef, useState } from "react";
import { api } from "../api";
import type { DocumentSummary, Folder } from "../api/types";
import { ApiError } from "../api/types";
import { Button, SelectField, TextField } from "../compose/controls";
import { Dialog } from "../compose/Dialog";
import { FileText, Plus } from "../compose/Icon";
import { useToast } from "../compose/Toast";
import {
  UPLOAD_ACCEPT,
  baseName,
  fileRejectReason,
  formatFromName,
  readFileAsBase64,
} from "../lib/fileIngest";

/**
 * Upload document — the "clients/staff upload briefings" flow (brief §client-
 * briefings). Pick a .docx/.md/.html file, a target folder, and a title; the
 * file is base64-encoded and POSTed to `/workspaces/:slug/documents`, where the
 * backend renders it (docz pipeline) and stores it as a DRAFT briefing. On
 * success the new document is handed back so the Library can show it immediately.
 *
 * The file→format→base64 helpers moved to `lib/fileIngest` when
 * `<NewArticleDialog>` became the second consumer — one extension table, one
 * `FileReader`, ONE size cap. The cap is new here and it closes a real hole:
 * this dialog used to hand a 5 MB .docx straight to `fetch`, where ctesibiuz's
 * 1 MiB JSON-body limit rejected it before any handler ran, and the user saw a
 * bare "Upload failed."
 */

const ROOT_FOLDER = "";

/** The formats the LIBRARY upload accepts (the article-brief dialog accepts a
 *  narrower set — the owner named docx and md there). */
const UPLOAD_FORMATS = ["docx", "md", "html"] as const;

export function UploadDialog({
  slug,
  folders,
  onClose,
  onUploaded,
}: {
  slug: string;
  folders: Folder[];
  onClose: () => void;
  onUploaded: (doc: DocumentSummary) => void;
}) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [folder, setFolder] = useState(ROOT_FOLDER);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const folderOptions = useMemo(
    () => [
      { value: ROOT_FOLDER, label: "No folder (workspace root)" },
      ...folders.map((f) => ({ value: f.name, label: f.name })),
    ],
    [folders],
  );

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    // Validate on PICK, not only on submit: the user learns the file is wrong
    // while they are still looking at the picker, not after filling the form.
    const reason = picked
      ? fileRejectReason(picked, { formats: UPLOAD_FORMATS })
      : null;
    setFile(reason ? null : picked);
    setError(reason);
    if (!reason && picked && !title.trim()) setTitle(baseName(picked.name));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError("Choose a .docx, .md, or .html file to upload.");
      return;
    }
    if (!title.trim()) {
      setError("Give the document a title.");
      return;
    }
    const reason = fileRejectReason(file, { formats: UPLOAD_FORMATS });
    if (reason) {
      setError(reason);
      return;
    }
    const format = formatFromName(file.name);
    if (!format) {
      setError("Unsupported file type. Upload a .docx, .md, or .html file.");
      return;
    }
    setBusy(true);
    try {
      const content_b64 = await readFileAsBase64(file);
      const doc = await api.uploadDocument(slug, {
        title: title.trim(),
        folder,
        format,
        filename: file.name,
        lang: "",
        content_b64,
      });
      toast.show(`Uploaded “${doc.title}”`);
      onUploaded(doc);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "Upload failed."
          : "Could not upload the document.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Upload a briefing"
      subtitle="Add a .docx, .md, or .html document to this workspace as a draft."
      onClose={onClose}
      testid="upload-dialog"
    >
      <form onSubmit={submit}>
        {error ? (
          <div className="auth__error" role="alert" data-testid="upload-error">
            {error}
          </div>
        ) : null}

        <div className="field">
          <label className="field__label" htmlFor="upload-file">
            Document file
          </label>
          <input
            ref={fileRef}
            id="upload-file"
            className="field__input"
            type="file"
            accept={UPLOAD_ACCEPT}
            data-testid="upload-file"
            onChange={onPick}
          />
          {file ? (
            <span className="field__hint" data-testid="upload-filename">
              <FileText size={13} strokeWidth={1.5} /> {file.name}
            </span>
          ) : null}
        </div>

        <TextField
          label="Title"
          placeholder="Q3 Casino Briefing"
          value={title}
          data-testid="upload-title"
          onChange={(e) => setTitle(e.target.value)}
        />

        <SelectField
          label="Folder"
          options={folderOptions}
          value={folder}
          data-testid="upload-folder"
          onChange={(e) => setFolder(e.target.value)}
        />

        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={busy}
            data-testid="upload-submit"
            icon={<Plus size={15} strokeWidth={1.5} />}
          >
            {busy ? "Uploading…" : "Upload document"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
